/*
 * SEMA search autocomplete.
 * Debounced typeahead backed by the Shopify Storefront API `search` query —
 * the API twin of the /search results page, so it matches the year/make/model
 * tags on products (mid-range years like "1949 Porsche" suggest correctly).
 * No app server / database involved: this talks straight to Shopify.
 */
(function () {
  "use strict";

  var API_VERSION = "2025-01";
  var DEBOUNCE_MS = 180;
  var MIN_CHARS = 2;
  var LIMIT = 8;

  // Shared product fields for both query shapes below.
  var FIELDS =
    "      title handle" +
    "      featuredImage { url(transform: {maxWidth: 96, maxHeight: 96}) altText }" +
    "      priceRange { minVariantPrice { amount currencyCode } }";

  // Vehicle search: tag-scoped products() query. Deterministic — a product only
  // matches on its own tags/title, with NO fuzzy expansion (the reason native
  // search wrongly pulled Pontiac GTOs into "firebird"). Ranking is ours to
  // control here via sortKey (default RELEVANCE for now).
  var PRODUCTS_QUERY =
    "query TagSearch($q: String!, $n: Int!) {" +
    "  products(first: $n, query: $q) {" +
    "    nodes {" + FIELDS + " }" +
    "  }" +
    "}";

  // Part-number lookup: the Storefront products() query can't filter by SKU, but
  // search() indexes it. Used only when the input looks like a part number.
  var SEARCH_QUERY =
    "query PartSearch($q: String!, $n: Int!) {" +
    "  search(query: $q, types: [PRODUCT], first: $n) {" +
    "    edges { node { ... on Product {" + FIELDS + " } } }" +
    "  }" +
    "}";

  // Make/model nicknames the catalog doesn't store literally (it's tagged
  // "Chevrolet", not "Chevy"). A token matching a key ALSO searches its canonical
  // form. Small + stable — add a line whenever a new nickname comes up.
  var ALIASES = {
    chevy: "chevrolet",
    vw: "volkswagen",
    olds: "oldsmobile",
    caddy: "cadillac",
    bimmer: "bmw",
    beemer: "bmw",
    vette: "corvette",
    cuda: "barracuda",
    merc: "mercedes"
  };

  // Split on whitespace/commas only — hyphens stay inside a token so model codes
  // like "f-150" survive as one unit (separatorVariants handles their spellings).
  function tokenize(s) {
    return String(s || "").toLowerCase().trim().split(/[\s,]+/).filter(Boolean);
  }

  // A pure run of 5+ digits is a part number (years are 4 digits → left as tags).
  function looksLikePartNumber(raw) {
    return /^\d{5,}$/.test(String(raw).trim());
  }

  // Spelling variants so "F150" / "F-150" / "F 150" all match. The data is
  // inconsistent (tag "F-150" vs title "F150"), so try: as-typed, collapsed (no
  // separators), and hyphenated at letter/digit boundaries. Shopify tag matching
  // treats hyphen == space, so the spaced form is covered by the hyphenated one.
  // Output is sanitized to [a-z0-9 -], so it's safe to quote into the query.
  function separatorVariants(term) {
    var orig = term.replace(/[^a-z0-9 -]/g, "");
    var collapsed = term.replace(/[^a-z0-9]/g, "");
    var hyph = collapsed.replace(/([a-z])([0-9])/g, "$1-$2").replace(/([0-9])([a-z])/g, "$1-$2");
    var out = [];
    [orig, collapsed, hyph].forEach(function (v) { if (v && out.indexOf(v) < 0) out.push(v); });
    return out;
  }

  // One token -> OR of (tag:"v" OR title:*v*) across its alias + spelling variants.
  function tokenClause(tok) {
    var forms = [tok];
    var key = tok.replace(/[^a-z0-9]/g, ""); // collapsed form, for alias lookup
    if (ALIASES[key]) forms.push(ALIASES[key]);
    var terms = [];
    forms.forEach(function (f) {
      separatorVariants(f).forEach(function (v) {
        var tagT = 'tag:"' + v + '"';
        var titleT = "title:*" + v + "*";
        if (terms.indexOf(tagT) < 0) terms.push(tagT);
        if (terms.indexOf(titleT) < 0) terms.push(titleT);
      });
    });
    return "(" + terms.join(" OR ") + ")";
  }

  // Each token must match (as tag or title); tokens are ANDed.
  function buildTagQuery(tokens) {
    return tokens.map(tokenClause).join(" AND ");
  }

  // The query string to hand the /search results page. Shopify's native search
  // honors this tag/title syntax in ?q=, so the full results page stays as
  // deterministic as the dropdown (a raw "firebird" would fuzzy-match GTOs).
  // Part numbers pass through as-is (native search indexes SKU).
  function builtQueryFor(raw) {
    if (looksLikePartNumber(raw)) return raw.trim();
    var toks = tokenize(raw);
    return toks.length ? buildTagQuery(toks) : raw.trim();
  }

  // Results URL: q = the tag-scoped query that drives matching; qh = the human
  // term, used only to keep the search box + heading readable (the raw tag query
  // is ugly). Shopify Liquid can't read qh, so the display fix is done in JS below.
  function resultsHref(action, raw) {
    return action +
      "?q=" + encodeURIComponent(builtQueryFor(raw)) +
      "&qh=" + encodeURIComponent(raw);
  }

  function money(amount, currency) {
    var n = parseFloat(amount);
    if (isNaN(n)) return "";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency || "USD",
      }).format(n);
    } catch (e) {
      return n.toFixed(2);
    }
  }

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function initInstance(root) {
    // Guard against double-init (the snippet — and thus its script tag — can
    // appear more than once on a page, e.g. header + search page).
    if (root.dataset.semaAcReady) return;
    root.dataset.semaAcReady = "1";

    var shop = root.getAttribute("data-shop");
    var token = root.getAttribute("data-token");
    var input = root.querySelector("[data-sema-ac-input]");
    var results = root.querySelector("[data-sema-ac-results]");
    if (!shop || !token || !input || !results) return;

    var endpoint = "https://" + shop + "/api/" + API_VERSION + "/graphql.json";
    var timer = null;
    var controller = null;
    var activeIndex = -1; // -1 = nothing; last index = the "see all" row
    var items = []; // array of clickable anchors currently rendered

    function close() {
      results.hidden = true;
      results.innerHTML = "";
      activeIndex = -1;
      items = [];
    }

    function open() {
      results.hidden = false;
    }

    function seeAllHref(q) {
      return resultsHref(root.querySelector(".sema-ac__form").getAttribute("action"), q);
    }

    function render(products, q) {
      var html = "";
      if (products.length === 0) {
        html += '<div class="sema-ac__empty">No matches for &ldquo;' + esc(q) + '&rdquo;</div>';
      } else {
        for (var i = 0; i < products.length; i++) {
          var p = products[i];
          var img = p.featuredImage && p.featuredImage.url;
          var price =
            p.priceRange && p.priceRange.minVariantPrice
              ? money(p.priceRange.minVariantPrice.amount, p.priceRange.minVariantPrice.currencyCode)
              : "";
          html +=
            '<a class="sema-ac__item" href="/products/' + esc(p.handle) + '">' +
            (img ? '<img class="sema-ac__thumb" src="' + esc(img) + '" alt="' + esc((p.featuredImage && p.featuredImage.altText) || "") + '">' : '<span class="sema-ac__thumb"></span>') +
            '<span class="sema-ac__meta">' +
            '<span class="sema-ac__title">' + esc(p.title) + "</span>" +
            "</span>" +
            (price ? '<span class="sema-ac__price">' + esc(price) + "</span>" : "") +
            "</a>";
        }
      }
      html +=
        '<a class="sema-ac__all" href="' + esc(seeAllHref(q)) + '">' +
        "See all results for &ldquo;" + esc(q) + "&rdquo; &rarr;</a>";
      results.innerHTML = html;
      items = Array.prototype.slice.call(results.querySelectorAll("a"));
      activeIndex = -1;
      open();
    }

    function fetchResults(q) {
      if (controller) controller.abort();
      controller = new AbortController();

      // Part number -> native search (indexes SKU); otherwise tag-scoped products().
      var isPart = looksLikePartNumber(q);
      var tokens = tokenize(q);
      if (!isPart && !tokens.length) { close(); return; }
      var gqlQuery = isPart ? SEARCH_QUERY : PRODUCTS_QUERY;
      var qStr = isPart ? q.trim() : buildTagQuery(tokens);

      fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({ query: gqlQuery, variables: { q: qStr, n: LIMIT } }),
        signal: controller.signal,
      })
        .then(function (r) { return r.json(); })
        .then(function (json) {
          var data = (json && json.data) || {};
          // products() returns nodes; search() returns edges.node — normalize.
          var products;
          if (data.products) {
            products = data.products.nodes || [];
          } else {
            var edges = (data.search && data.search.edges) || [];
            products = edges.map(function (e) { return e.node; }).filter(Boolean);
          }
          // Ignore if the input changed out from under us.
          if (input.value.trim() !== q) return;
          render(products, q);
        })
        .catch(function (err) {
          if (err && err.name === "AbortError") return;
          // On any failure, stay out of the way — the form still submits to /search.
          close();
        });
    }

    function onInput() {
      var q = input.value.trim();
      if (timer) clearTimeout(timer);
      if (q.length < MIN_CHARS) { close(); return; }
      timer = setTimeout(function () { fetchResults(q); }, DEBOUNCE_MS);
    }

    function setActive(idx) {
      if (!items.length) return;
      if (idx < 0) idx = items.length - 1;
      if (idx >= items.length) idx = 0;
      for (var i = 0; i < items.length; i++) items[i].classList.remove("is-active");
      activeIndex = idx;
      items[activeIndex].classList.add("is-active");
      items[activeIndex].scrollIntoView({ block: "nearest" });
    }

    function onKeyDown(e) {
      if (results.hidden || !items.length) return;
      if (e.key === "ArrowDown") { e.preventDefault(); setActive(activeIndex + 1); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActive(activeIndex - 1); }
      else if (e.key === "Enter") {
        if (activeIndex >= 0 && items[activeIndex]) {
          e.preventDefault();
          window.location.href = items[activeIndex].getAttribute("href");
        }
        // else: let the form submit to /search normally.
      } else if (e.key === "Escape") {
        close();
      }
    }

    input.addEventListener("input", onInput);
    input.addEventListener("keydown", onKeyDown);
    input.addEventListener("focus", function () {
      if (input.value.trim().length >= MIN_CHARS && results.innerHTML) open();
    });
    document.addEventListener("click", function (e) {
      if (!root.contains(e.target)) close();
    });

    // Submit (Enter with no item highlighted, or the search button) must go to
    // the tag-scoped results URL, not raw text — otherwise the results page
    // reverts to Shopify's fuzzy search and the false matches come back.
    var form = root.querySelector(".sema-ac__form");
    if (form) {
      form.addEventListener("submit", function (e) {
        var raw = input.value.trim();
        if (!raw) return; // empty: let the browser do its default thing
        e.preventDefault();
        window.location.href = resultsHref(form.getAttribute("action"), raw);
      });
    }
  }

  // Reconstruct the human term from a tag query, e.g.
  // "(tag:1949 OR title:*1949*) AND (tag:porsche OR title:*porsche*)" -> "1949 porsche".
  // Needed because Shopify's pagination links carry q= but drop our qh=, so page 2+
  // (and bookmarks) have no qh to display — we derive it from q instead.
  function deriveHumanTerm(q) {
    if (!q) return "";
    if (q.indexOf('tag:"') === -1) return q; // part number / plain text: use as-is
    // One AND-clause per token; its first quoted tag value is the user's own form.
    var out = [];
    q.split(" AND ").forEach(function (clause) {
      var m = clause.match(/tag:"([^"]+)"/);
      if (m && out.indexOf(m[1]) < 0) out.push(m[1]);
    });
    return out.length ? out.join(" ") : q;
  }

  // On the results page the URL carries q=<tag query> (ugly). Prefer qh=<human term>
  // when present; otherwise derive it from q. Swap it into the box + heading.
  function applyFriendlyTerm() {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    var qh = params.get("qh") || deriveHumanTerm(params.get("q"));
    if (!qh) return;
    var inputs = document.querySelectorAll("[data-sema-ac-input]");
    for (var i = 0; i < inputs.length; i++) inputs[i].value = qh;
    var el = document.querySelector("[data-sema-terms]");
    if (el) el.textContent = qh;
  }

  function init() {
    var roots = document.querySelectorAll("[data-sema-ac]");
    for (var i = 0; i < roots.length; i++) initInstance(roots[i]);
    applyFriendlyTerm();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
